<!-- src/views/client/Dashboard.vue -->
<template>
  <client-layout :title="$t('tableauDeBord.titre')">
    <div v-if="loading" class="flex justify-center items-center h-64">
      <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
    </div>

    <div v-else>
      <div class="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div class="flex items-center gap-3">
          <h2 class="text-xl font-semibold text-gray-800">{{ $t('tableauDeBord.mesDispositifs') }}</h2>
          <router-link v-if="estTitulaire" to="/client/claim"
                       class="px-3 py-1.5 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700">
            {{ $t('tableauDeBord.ajouterAppareil') }}
          </router-link>
        </div>

        <!-- Préférence MAJ auto / manuel — réglage du compte, donc du titulaire -->
        <label v-if="estTitulaire" class="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
          <span>{{ $t('tableauDeBord.majAuto') }}</span>
          <button type="button" role="switch" :aria-checked="otaAuto"
                  @click="toggleOtaAuto" :disabled="savingAuto"
                  :class="otaAuto ? 'bg-primary-600' : 'bg-gray-300'"
                  class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50">
            <span :class="otaAuto ? 'translate-x-6' : 'translate-x-1'"
                  class="inline-block h-4 w-4 transform rounded-full bg-white transition-transform"></span>
          </button>
          <span class="text-xs text-gray-400 w-12">{{ otaAuto ? $t('tableauDeBord.auto') : $t('tableauDeBord.manuel') }}</span>
        </label>
      </div>

      <i18n-t v-if="estTitulaire" keypath="tableauDeBord.explicationModes" tag="p" class="text-xs text-gray-500 -mt-3 mb-6">
        <template #automatique><strong>{{ $t('tableauDeBord.modeAutomatique') }}</strong></template>
        <template #manuel><strong>{{ $t('tableauDeBord.modeManuel') }}</strong></template>
      </i18n-t>

      <div v-if="devices.length === 0" class="bg-white rounded-lg shadow p-8 text-center">
        <p class="text-gray-700 font-medium">{{ $t('tableauDeBord.aucunAppareil') }}</p>
        <p class="mt-2 text-sm text-gray-500 max-w-md mx-auto">
          {{ $t('tableauDeBord.aucunAppareilAide') }}
        </p>
        <router-link v-if="estTitulaire" to="/client/claim"
                     class="mt-4 inline-block px-4 py-2 rounded-md bg-primary-600 text-white hover:bg-primary-700">
          {{ $t('tableauDeBord.rattacher') }}
        </router-link>
        <p v-else class="mt-2 text-sm text-gray-500">{{ $t('tableauDeBord.aucunAppareilMembre') }}</p>
      </div>

      <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        <div v-for="device in devices" :key="device.id"
             class="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow"
             @click="$router.push(`/client/devices/${device.id}`)">
          <div class="flex justify-between items-start mb-2">
            <div>
              <h3 class="font-semibold text-gray-900">{{ device.name }}</h3>
              <p class="text-sm text-gray-500">{{ device.location }}</p>
            </div>
            <span :class="`px-2 py-1 text-xs font-semibold rounded-full ${getStateBadge(device)}`">
              {{ getStateLabel(device) }}
            </span>
          </div>

          <alarme-non-vue class="mt-2" :device="device" compact @vue="device.alarm_unseen = null" />

          <div class="flex gap-3 mt-3 text-xs text-gray-500">
            <span v-if="device.bat_pct !== null && device.bat_pct !== undefined">
              🔋 {{ device.bat_pct }}%<span v-if="device.cap" class="font-normal text-gray-500"> · {{ device.cap }} mA·h</span>
            </span>
            <span v-if="device.solar_mode || device.power_source === 'solar'">☀️ {{ $t('etat.solar') }}</span>
            <span v-else-if="device.power_source === 'mains'">⚡ {{ $t('etat.mains') }}</span>
            <span v-else-if="device.power_source === 'battery'">🔌 {{ $t('etat.battery') }}</span>
          </div>

          <!-- Bannière mise à jour OTA -->
          <div v-if="device.ota" class="mt-3 rounded-md border p-2.5 text-xs"
               :class="device.ota.ready ? 'border-primary-200 bg-primary-50' : 'border-gray-200 bg-gray-50'"
               @click.stop>
            <div class="flex items-center justify-between gap-2">
              <span class="font-medium text-gray-700">
                <!-- Jamais système armé (06/10/2026) : l'installation attend le désarmement. -->
                <template v-if="device.ota.attente_desarmement">
                  ⏸ {{ $t('tableauDeBord.installationAuDesarmement', { cible: device.ota.target_label }) }}
                </template>
                <template v-else-if="device.ota.ready && device.ota.install_requested">
                  ⏳ {{ $t('tableauDeBord.installationEnCours', { cible: device.ota.target_label }) }}
                </template>
                <template v-else-if="device.ota.ready">
                  ⬆️ {{ $t('tableauDeBord.majPrete', { cible: device.ota.target_label }) }}
                </template>
                <template v-else>
                  ⬇️ {{ $t('tableauDeBord.telechargementEnCours', { cible: device.ota.target_label }) }}
                </template>
              </span>

              <button v-if="estTitulaire && device.ota.ready && !device.ota.install_requested"
                      @click="installNow(device)" :disabled="installing === device.id"
                      class="px-2.5 py-1 rounded bg-primary-600 text-white text-xs font-medium hover:bg-primary-700 disabled:opacity-50">
                {{ installing === device.id ? '…' : $t('tableauDeBord.installer') }}
              </button>
            </div>
            <p v-if="device.ota.ready && !device.ota.install_requested" class="mt-1 text-gray-400">
              {{ $t('tableauDeBord.redemarrage') }}
            </p>
            <p v-if="device.ota.imposee" class="mt-1 text-gray-500">{{ $t('tableauDeBord.majImposee') }}</p>
          </div>

          <!-- Injoignable (05/10/2026) : selon la règle du serveur, propre à la cadence de rapport
               de la carte — jamais recalculée ici. -->
          <div v-if="device.last_seen && device.presence && device.presence.hors_ligne"
               class="mt-2 inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
            <span class="h-1.5 w-1.5 rounded-full bg-gray-500"></span>
            {{ $t('tableauDeBord.injoignable', { quand: formatDate(device.last_seen) }) }}
          </div>
          <div v-else-if="device.last_seen" class="mt-2 text-xs text-gray-400">
            {{ formatDate(device.last_seen) }}
          </div>
        </div>
      </div>

      <h2 class="text-xl font-semibold text-gray-800 mb-4">{{ $t('tableauDeBord.derniersEvenements') }}</h2>
      <!-- Liste tous appareils confondus : sans ce drapeau, rien ne dit de quelle carte
           provient chaque ligne. Le tableau de bord admin le passait déjà. -->
      <event-list :events="events" :show-device-info="true" more-link="/client/events" />
    </div>
  </client-layout>
</template>

<script>
import { defineComponent, ref, onMounted, onUnmounted , computed} from 'vue';
import { useStore } from 'vuex';
import { format, parseISO } from 'date-fns';
import { useI18n } from 'vue-i18n';
import { localeDateFns, enParis } from '@/i18n/dates';
import { texteErreur, texteSucces } from '@/i18n/serveur';
import ClientLayout from '@/components/layout/ClientLayout.vue';
import EventList from '@/components/common/EventList.vue';
import apiService from '@/services/api.service';
import socketService from '@/services/socket.service';
import AlarmeNonVue from '@/components/device/AlarmeNonVue.vue';

export default defineComponent({
  name: 'ClientDashboard',
  components: { ClientLayout, EventList, AlarmeNonVue },

  setup() {
    const store = useStore();
    const { t } = useI18n();
    // Comptes partagés : un membre ne rattache pas d'appareil et ne règle pas les mises à jour.
    const estTitulaire = computed(() => { const u = store.getters['auth/user']; return !u || !u.memberRole || u.memberRole === 'titulaire'; });

    const loading = ref(true);
    const devices = ref([]);
    const events  = ref([]);
    const otaAuto = ref(false);
    const savingAuto = ref(false);
    const installing = ref(null);

    let socket = null;

    const load = async () => {
      const result = await store.dispatch('client/fetchDashboard');
      devices.value = result.devices || [];
      events.value  = result.events  || [];
      otaAuto.value = !!result.ota_auto;
    };

    onMounted(async () => {
      try {
        await load();
      } catch (e) {
        console.error(e);
      } finally {
        loading.value = false;
      }
      // Temps réel : toute évolution de campagne OTA rafraîchit le tableau de bord.
      try {
        socket = socketService.getSocket();
        socket.on('ota:update', onOtaUpdate);
        socket.on('device:update', onDeviceUpdate);
      } catch (e) { /* ws optionnel */ }
    });

    onUnmounted(() => {
      if (socket) { socket.off('ota:update', onOtaUpdate); socket.off('device:update', onDeviceUpdate); }
    });

    const onOtaUpdate = () => { load().catch(() => {}); };
    // Temps réel : l'état, la batterie et l'alarme non vue (B11) d'une carte se mettent à jour
    // sans recharger — une alarme doit passer au rouge sur le tableau de bord dès qu'elle arrive.
    const onDeviceUpdate = (data) => {
      const d = devices.value.find(x => x.id === data.deviceId);
      if (d && data.fields) Object.assign(d, data.fields);
    };

    const toggleOtaAuto = async () => {
      const next = !otaAuto.value;
      savingAuto.value = true;
      try {
        await apiService.setClientOtaAutoPref(next);
        otaAuto.value = next;
      } catch (e) {
        alert(texteErreur(e, t('tableauDeBord.echecPreference')));
      } finally {
        savingAuto.value = false;
      }
    };

    const installNow = async (device) => {
      installing.value = device.id;
      try {
        const { data } = await apiService.installClientOta(device.id);
        // Système armé : la mise à jour partira au prochain désarmement — on le dit.
        if (data && data.attente === 'desarmement') alert(texteSucces(data, t('tableauDeBord.installationAuDesarmement', { cible: device.ota.target_label })));
        await load();
      } catch (e) {
        alert(texteErreur(e, t('tableauDeBord.echecInstallation')));
      } finally {
        installing.value = null;
      }
    };

    const formatDate = (d) => {
      if (!d) return '';
      try { return format(enParis(parseISO(d)), "dd MMM yyyy HH:mm", { locale: localeDateFns() }); }
      catch { return d; }
    };

    const getStateBadge = (device) => {
      switch (device.state) {
        case 'armed':     return 'bg-orange-100 text-orange-700';
        case 'triggered': return 'bg-red-100 text-red-700';
        case 'disarmed':  return 'bg-green-100 text-green-700';
        default:          return 'bg-gray-100 text-gray-500';
      }
    };

    const getStateLabel = (device) => {
      switch (device.state) {
        case 'armed':     return t('etat.armed');
        case 'triggered': return t('etat.triggered');
        case 'disarmed':  return t('etat.disarmed');
        // Aucun message reçu depuis le rattachement : « Inconnu » laissait croire à une panne,
        // alors que la carte n'a simplement pas encore été allumée (ou pas encore parlé).
        default:          return device.last_seen ? t('etat.unknown') : t('etat.jamaisConnectee');
      }
    };


    return {
      estTitulaire,
      loading, devices, events, otaAuto, savingAuto, installing,
      toggleOtaAuto, installNow,
      formatDate, getStateBadge, getStateLabel
    };
  }
});
</script>
